import React, { useEffect, useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  Textarea,
  Select,
  Grid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  IconButton,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Image,
  Divider,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FiEdit, FiTrash2, FiPlus, FiEye } from 'react-icons/fi';
import { apiService } from '../ExternalApiService';
import templateDetailsIcon from '../assets/iconsetup.png';
import { MotionVStack, MotionGrid, MotionCard, MotionBox, variants } from '../components/motion';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState('');

  const toast = useToast();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const data = await apiService.fetchTemplates();
      setTemplates(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch templates',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCreateTemplate = async () => {
    if (!templateName.trim() || !templateContent.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in both name and content',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const newTemplate = await apiService.createTemplate({
        name: templateName,
        content: templateContent,
      });
      setTemplates([...templates, newTemplate]);
      setTemplateName('');
      setTemplateContent('');
      setIsCreating(false);
      toast({
        title: 'Success',
        description: 'Template created successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to create template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTemplate = async () => {
    if (!selectedTemplate || !templateName.trim() || !templateContent.trim()) {
      return;
    }

    setIsLoading(true);
    try {
      await apiService.updateTemplate(selectedTemplate.id, {
        name: templateName,
        content: templateContent,
      });
      setTemplates(templates.map(t =>
        t.id === selectedTemplate.id
          ? { ...t, name: templateName, content: templateContent }
          : t
      ));
      setIsEditing(false);
      setSelectedTemplate(null);
      toast({
        title: 'Success',
        description: 'Template updated successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    setIsLoading(true);
    try {
      await apiService.deleteTemplate(templateId);
      setTemplates(templates.filter(t => t.id !== templateId));
      toast({
        title: 'Success',
        description: 'Template deleted successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateContent(template.content);
    setIsEditing(true);
  };

  const handlePreviewTemplate = async (template) => {
    try {
      const preview = await apiService.previewTemplate(template.id);
      setPreviewData(preview);
      setIsPreviewOpen(true);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to preview template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const resetForm = () => {
    setTemplateName('');
    setTemplateContent('');
    setSelectedTemplate(null);
    setIsEditing(false);
    setIsCreating(false);
  };

  return (
    <MotionVStack spacing={6} align="stretch" variants={variants.container} initial="hidden" animate="show">
      <MotionBox variants={variants.item}>
        <HStack justify="space-between" align="center">
          <Heading size="lg">Templates</Heading>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={() => setIsCreating(true)}
          >
            Create Template
          </Button>
        </HStack>
        <Text color="gray.600" mt={2}>
          Manage your message templates
        </Text>
      </MotionBox>

      {templates.length === 0 ? (
        <MotionBox variants={variants.item}>
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            Nothing here. Please add a template.
          </Alert>
        </MotionBox>
      ) : (
        <MotionGrid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }} gap={4} variants={variants.container}>
          {templates.map((template) => (
            <MotionCard key={template.id} variant="outline" h="full" display="flex" flexDirection="column" variants={variants.item}>
              <CardHeader>
                <HStack justify="space-between">
                  <Heading size="sm">{template.name}</Heading>
                  <Badge colorScheme="green" variant="subtle">
                    Active
                  </Badge>
                </HStack>
              </CardHeader>
              <CardBody flex="1" display="flex" flexDirection="column">
                <Text fontSize="sm" color="gray.600" noOfLines={3} flex="1">
                  {template.content}
                </Text>
                <HStack mt={4} spacing={2} justify="flex-end">
                  <IconButton
                    icon={<FiEye />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handlePreviewTemplate(template)}
                  />
                  <IconButton
                    icon={<FiEdit />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditTemplate(template)}
                  />
                  <IconButton
                    icon={<FiTrash2 />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => handleDeleteTemplate(template.id)}
                  />
                </HStack>
              </CardBody>
            </MotionCard>
          ))}
        </MotionGrid>
      )}

      <Modal isOpen={isCreating || isEditing} onClose={resetForm} size="6xl">
        <ModalOverlay />
        <ModalContent css={{ borderTopRightRadius: 0, borderTopLeftRadius: 0 }}>
          <ModalHeader css={{ height: 16, width: 'full', background: '#E8ECF4' }}>
            <HStack>
              <Image
                src={templateDetailsIcon}
                alt="template details icon"
                h={3}
                w="auto"
              />
              <Text fontSize="sm">Template Details</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody css={{ paddingTop: isEditing ? "64px" : "96px", paddingBottom: isEditing ? "64px" : "96px" }} display="flex" justifyContent="center">
            <VStack spacing={4} align="stretch" maxW="xl" w="full" padding={4} background="#E8ECF4">
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Template Name</FormLabel>
                <Input
                  placeholder="Template Name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  background={"white"}
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Description</FormLabel>
                <Textarea
                  placeholder="My name is {{name}} and I am {{age}} years old.
                  I live in {{city}}.
                  I work at {{company}}.
                  I am a {{job}}.
                  I have {{children}} children."
                  size="lg"
                  resize="none"
                  h="200px"
                  value={templateContent}
                  onChange={(e) => setTemplateContent(e.target.value)}
                  background={"white"}
                />
              </FormControl>

              {isEditing && (
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">Select Template</FormLabel>
                  <Select
                    placeholder="Select template"
                    value={selectedTemplate?.id || ''}
                    onChange={(e) => {
                      const template = templates.find(t => t.id === e.target.value);
                      if (template) {
                        setSelectedTemplate(template);
                        setTemplateName(template.name);
                        setTemplateContent(template.content);
                      }
                    }}
                    background="white"
                  >
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
            </VStack>
          </ModalBody>
          <Divider orientation="horizontal" h={2} borderColor="gray.300" />
          <ModalFooter>
            <HStack justify="space-between" width="full">
              <Button variant="solid" onClick={resetForm} colorScheme='red'>
                Cancel
              </Button>
              <Button
                css={{ background: 'black' }}
                color={'white'}
                onClick={isCreating ? handleCreateTemplate : handleUpdateTemplate}
                isLoading={isLoading}
              >
                Confirm
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Template Preview</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text whiteSpace="pre-wrap">{previewData}</Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setIsPreviewOpen(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </MotionVStack>
  );
};

export default Templates;
